import { classify } from 'sidecar-utilities'

import { Sentiment } from '@sidecar-components/types'

const iconColors = ({
  disabled,
  sentiment,
}: {
  disabled?: boolean
  sentiment?: Sentiment
}) => {
  switch (sentiment) {
    case Sentiment.PRIMARY:
      return classify({
        base: disabled ? 'color-iconPrimary-disabled' : 'color-iconPrimary',
        hover: !disabled && 'group-hover/button:color-iconPrimary-hover',
        active: !disabled && 'group-active/button:color-iconPrimary-active',
        focus: !disabled && 'group-focus/button:color-iconPrimary-active',
      })
    case Sentiment.ENGAGE:
      return classify({
        base: disabled
          ? 'color-iconEngage-disabled'
          : 'color-iconEngage-default',
        hover: !disabled && 'group-hover/button:color-iconEngage-hover',
        active: !disabled && 'group-active/button:color-iconEngage-active',
        focus: !disabled && 'group-focus/button:color-iconEngage-active',
      })
    case Sentiment.ENGAGE_ON_COLOR:
      return classify({
        base: disabled
          ? 'color-iconEngageOnBrand-disabled'
          : 'color-iconEngageOnBrand-default',
        hover: !disabled && 'group-hover/button:color-iconEngageOnBrand-hover',
        active:
          !disabled && 'group-active/button:color-iconEngageOnBrand-active',
        focus: !disabled && 'group-focus/button:color-iconEngageOnBrand-active',
      })
    case Sentiment.ACCENT:
      return classify({
        base: disabled
          ? 'color-iconOnAccent-disabled'
          : 'color-iconOnAccent-default',
        hover: !disabled && 'group-hover/button:color-iconOnAccent-hover',
        active: !disabled && 'group-active/button:color-iconOnAccent-active',
        focus: !disabled && 'group-focus/button:color-iconOnAccent-active',
      })
    case Sentiment.NOTIFY:
      return classify({
        icon: disabled
          ? 'color-iconOnNotify-disabled'
          : 'color-iconOnNotify-default',
        hover: !disabled && 'group-hover/button:color-iconOnNotify-hover',
        active: !disabled && 'group-active/button:color-iconOnNotify-active',
        focus: !disabled && 'group-focus/button:color-iconOnNotify-active',
      })
    case Sentiment.RISK:
      return classify({
        icon: disabled
          ? 'color-iconOnRisk-disabled'
          : 'color-iconOnRisk-default',
        hover: !disabled && 'group-hover/button:color-iconOnRisk-hover',
        active: !disabled && 'group-active/button:color-iconOnRisk-active',
        focus: !disabled && 'group-focus/button:color-iconOnRisk-active',
      })
    case Sentiment.BRAND:
      return classify({
        icon: disabled
          ? 'color-iconOnBrand-disabled'
          : 'color-iconOnBrand-default',
        hover: !disabled && 'group-hover/button:color-iconOnBrand-hover',
        active: !disabled && 'group-active/button:color-iconOnBrand-active',
        focus: !disabled && 'group-focus/button:color-iconOnBrand-active',
      })
    case Sentiment.INVERSE:
      return classify({
        icon: disabled
          ? 'color-iconOnInverse-disabled'
          : 'color-iconOnInverse-default',
        hover: !disabled && 'group-hover/button:color-iconOnInverse-hover',
        active: !disabled && 'group-active/button:color-iconOnInverse-active',
        focus: !disabled && 'group-focus/button:color-iconOnInverse-active',
      })
    case Sentiment.DEFAULT:
    default:
      return classify({
        base: disabled ? 'color-icon-disabled' : 'color-icon-default',
        hover: !disabled && 'group-hover/button:color-icon-hover',
        active: !disabled && 'group-active/button:color-icon-active',
        focus: !disabled && 'group-focus/button:color-icon-hover',
      })
  }
}

const focusCircleColor = ({
  disabled,
  sentiment,
}: {
  disabled?: boolean
  sentiment?: Sentiment
}) => {
  switch (sentiment) {
    case Sentiment.ENGAGE:
      return !disabled
        ? `hover:after:color-fauxOutlineEngage-weak-default 
        focus:after:color-fauxOutlineEngage-weak-default 
        active:after:color-fauxOutlineEngage-default`
        : ''
    case Sentiment.ENGAGE_ON_COLOR:
      return !disabled
        ? `hover:after:color-fauxOutlineEngageOnBrand-default 
        focus:after:color-fauxOutlineEngageOnBrand-default 
        active:after:color-fauxOutlineEngageOnBrand-bold-default`
        : ''
    case Sentiment.ACCENT:
      return !disabled
        ? `hover:after:color-fauxOutlineAccent-weak-default 
        focus:after:color-fauxOutlineAccent-weak-default 
        active:after:color-fauxOutlineAccent-default`
        : ''
    case Sentiment.NOTIFY:
      return !disabled
        ? `hover:after:color-fauxOutlineNotify-weak-default [0.56]
        focus:after:color-fauxOutlineNotify-weak-default 
        active:after:color-fauxOutlineNotify-default`
        : ''
    case Sentiment.RISK:
      return !disabled
        ? `hover:after:color-fauxOutlineRisk-weak-default 
        focus:after:color-fauxOutlineRisk-weak-default 
        active:after:color-fauxOutlineRisk-default`
        : ''
    case Sentiment.BRAND:
      return !disabled
        ? `hover:after:color-fauxOutlineBrand-weak-default 
        focus:after:color-fauxOutlineBrand-weak-default 
        active:after:color-fauxOutlineBrand-default`
        : ''
    case Sentiment.INVERSE:
      return !disabled
        ? `hover:after:color-fauxOutlineInverse-default 
        focus:after:color-fauxOutlineInverse-default 
        active:after:color-fauxOutlineInverse-default`
        : ''
    case Sentiment.DEFAULT:
    default:
      return !disabled
        ? `hover:after:color-fauxOutlinePrimaryDecorWeak 
        focus:after:color-fauxOutlinePrimaryDecorWeak 
        active:after:color-fauxOutlinePrimaryDecor`
        : ''
  }
}

// styles for the button, which wraps the icon
export const buttonStyles = ({
  disabled,
  sentiment,
}: {
  disabled?: boolean
  sentiment?: Sentiment
}) =>
  classify({
    default: 'inline-block align-middle w-fit group/button relative shrink-0',
    cursor: disabled ? 'cursor-not-allowed' : 'cursor-pointer',
    focusState: 'focus:outline-none',
    // focus/hover/active styles adds a circle behind the icon that doesn't
    // take up actual space in the layout.
    circleColors: !disabled && focusCircleColor({ disabled, sentiment }),
    hoverCircle:
      !disabled &&
      `hover:after:absolute
      hover:after:rounded-full
      hover:after:-top-2
      hover:after:-left-2
      hover:after:w-[calc(100%+theme(space.4))]
      hover:after:h-[calc(100%+theme(space.4))]`,
    focusCircle:
      !disabled &&
      `focus:after:absolute
      focus:after:rounded-full
      focus:after:-top-2
      focus:after:-left-2
      focus:after:w-[calc(100%+theme(space.4))]
      focus:after:h-[calc(100%+theme(space.4))]`,
    activeCircle:
      !disabled &&
      `active:after:absolute
      active:after:rounded-full
      active:after:-top-2
      active:after:-left-2
      active:after:w-[calc(100%+theme(space.4))]
      active:after:h-[calc(100%+theme(space.4))]`,
  })

export const iconStyles = ({
  busy,
  disabled,
  sentiment,
}: {
  busy?: boolean
  disabled?: boolean
  sentiment?: Sentiment
}) =>
  classify({
    // makes sure the icon is above the focus circle
    layout: 'relative z-1',
    colors: iconColors({ disabled: busy || disabled, sentiment }),
    spinning: busy ? 'animate-spin' : '',
  })
